import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle, CheckCircle, Settings, Code, FileText, Database, Target, Clock, FileSearch } from 'lucide-react';
import TextHighlighter from './TextHighlighter';

const DateExtractionConfig = ({ extractedData, onDateConfigChange, initialConfig = {} }) => {
  const [dateConfig, setDateConfig] = useState({
    sourceType: 'json', // 'json', 'text', 'table', 'positional', 'current', 'filename'
    jsonKey: '',
    textPattern: '',
    regexPattern: '',
    useRegex: false,
    multiLine: false, // For multi-line text patterns
    componentExtraction: false, // For extracting date components from multiple lines
    dateComponents: {
      extractionMode: 'pattern', // 'pattern' or 'sequential'
      basePattern: '', // Text pattern to find before extracting components
      monthLine: '',
      dayLine: '',
      yearLine: '',
      monthPattern: '',
      dayPattern: '',
      yearPattern: '',
      // Sequential extraction settings
      monthOffset: 1, // Lines after base pattern (1 = next line)
      dayOffset: 2,   // Lines after base pattern
      yearOffset: 3   // Lines after base pattern
    },
    dateFormat: '%m/%d/%Y',
    dateOffset: 0, // Global date offset in days (applies to all date sources except 'current')
    fallbackEnabled: true,
    fallbackFormat: '%Y-%m-%d',
    fallbackRowLimit: 200, // Number of rows to send to OpenAI for fallback date extraction
    fallbackRowStart: 0, // Starting row for OpenAI fallback (0-indexed)
    fallbackRowEnd: 200, // Ending row for OpenAI fallback (exclusive)
    positionalConfig: null, // For positional date extraction
    validation: {
      enabled: true,
      minDate: '',
      maxDate: '',
      allowFuture: true
    },
    currentDateConfig: {
      useCurrentDate: false,
      dateOffset: 0, // Days to add/subtract from current date
      timeZone: 'local' // 'local', 'utc', or specific timezone
    },
    ...initialConfig
  });
  
  const [previewResult, setPreviewResult] = useState(null);
  const [availableKeys, setAvailableKeys] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [isSelectingPosition, setIsSelectingPosition] = useState(false);
  const [isConfigInitialized, setIsConfigInitialized] = useState(false);

  // Update dateConfig when initialConfig changes (only once)
  useEffect(() => {
    if (!isConfigInitialized && initialConfig && Object.keys(initialConfig).length > 0) {

      setDateConfig(prev => ({
        ...prev,
        ...initialConfig
      }));
      setIsConfigInitialized(true);
    }
  }, [initialConfig, isConfigInitialized]);

  // Common date format patterns from normalizers
  const formatPatterns = [
    { label: 'MM/DD/YYYY (12/08/2025)', value: '%m/%d/%Y', example: '12/08/2025' },
    { label: 'Dec 8, 2025', value: '%b %d, %Y', example: 'Dec 8, 2025' },
    { label: 'August 3 2025 (no comma)', value: '%B %d %Y', example: 'August 3 2025' },
    { label: 'DD-MMM-YY (30May25)', value: '%d%b%y', example: '30May25' },
    { label: 'MM-DD-YY (06-28-25)', value: '%m-%d-%y', example: '06-28-25' },
    { label: 'Monday December 8, 2025', value: '%A %B %d, %Y', example: 'Monday December 8, 2025' },
    { label: 'DD-MMM-YY (29-Jul-25)', value: '%d-%b-%y', example: '29-Jul-25' },
    { label: 'DD MMM YYYY (01 Mar 2025)', value: '%d %b %Y', example: '01 Mar 2025' },
    { label: 'YYYY-MM-DD (2025-12-08)', value: '%Y-%m-%d', example: '2025-12-08' },
    { label: 'Custom Format', value: 'custom', example: 'Enter your own format' }
  ];

  // Common regex patterns from normalizers
  const regexPatterns = [
    {
      label: 'Fuzzy Date Component Collection (Recommended)',
      value: '(?=.*DAILY\\s+FLASH\\s+REPORT)[\\s\\S]{0,200}?(?<month>January|February|March|April|May|June|July|August|September|October|November|December)[\\s\\S]{0,100}?(?<day>\\b(?:[1-9]|[12][0-9]|3[01])\\b)[\\s\\S]{0,100}?(?<year>\\b(?:20[0-9]{2})\\b)|(?=.*DAILY\\s+FLASH\\s+REPORT)[\\s\\S]{0,200}?(?<day>\\b(?:[1-9]|[12][0-9]|3[01])\\b)[\\s\\S]{0,100}?(?<month>January|February|March|April|May|June|July|August|September|October|November|December)[\\s\\S]{0,100}?(?<year>\\b(?:20[0-9]{2})\\b)|(?=.*DAILY\\s+FLASH\\s+REPORT)[\\s\\S]{0,200}?(?<year>\\b(?:20[0-9]{2})\\b)[\\s\\S]{0,100}?(?<month>January|February|March|April|May|June|July|August|September|October|November|December)[\\s\\S]{0,100}?(?<day>\\b(?:[1-9]|[12][0-9]|3[01])\\b)',
      description: 'Collects Month, Day, Year in any order within 200 chars after DAILY FLASH REPORT. Handles scattered components across multiple lines.'
    },
    { 
      label: 'Full Day/Date (Sunday, June 8, 2025)', 
      value: '(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),\\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\\s+\\d{1,2},\\s+\\d{4}',
      description: 'Matches full weekday and month names'
    },
    {
      label: 'Any-order Month/Day/Year across lines',
      value: '(?:(?<month>January|February|March|April|May|June|July|August|September|October|November|December)[\\s,]*[\\s\\S]{0,40}(?<day>\\d{1,2})[\\s\\S]{0,10}(?<year>\\d{4}))|(?:(?<day>\\d{1,2})[\\s\\S]{0,40}(?<month>January|February|March|April|May|June|July|August|September|October|November|December)[\\s\\S]{0,10}(?<year>\\d{4}))|(?:(?<year>\\d{4})[\\s\\S]{0,40}(?<month>January|February|March|April|May|June|July|August|September|October|November|December)[\\s\\S]{0,10}(?<day>\\d{1,2}))',
      description: 'Finds Month, Day, and Year in any order within a short window (handles lines out of order). Uses named groups month/day/year.'
    },
    {
      label: 'Multi-line Date Components (Month\\nDay\\nYear)',
      value: '(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\\s*\\n\\s*(January|February|March|April|May|June|July|August|September|October|November|December)\\s*\\n\\s*(\\d{1,2})\\s*\\n\\s*(\\d{4})',
      description: 'Extracts date from separate lines: Weekday, Month, Day, Year (e.g., Wednesday\\nAugust\\n6\\n2025)'
    },
    {
      label: 'Month Day Year on separate lines',
      value: '(January|February|March|April|May|June|July|August|September|October|November|December)\\s*\\n\\s*(\\d{1,2})\\s*\\n\\s*(\\d{4})',
      description: 'Extracts Month, Day, Year from consecutive lines (e.g., August\\n6\\n2025)'
    },
    { 
      label: 'Business Date Run Pattern (30May25 Run on)', 
      value: '(\\d{1,2}\\w{3}\\d{2})\\s+Run on',
      description: 'Extracts business date from Marriott-style reports'
    },
    { 
      label: 'Parenthetical Date ((01 Mar 2025))', 
      value: '\\((\\d{2} \\w{3} \\d{4})\\)',
      description: 'Extracts dates from parentheses'
    },
    { 
      label: 'MM-DD-YY or MM/DD/YYYY', 
      value: '\\d{2}[-/]\\d{2}[-/]\\d{2,4}',
      description: 'Matches standard date formats with slashes or dashes'
    },
    { 
      label: 'ISO Date (YYYY-MM-DD)', 
      value: '\\d{4}-\\d{2}-\\d{2}',
      description: 'Matches ISO 8601 date format'
    }
  ];

  // Extract available keys from JSON data
  useEffect(() => {
    // Check both camelCase and snake_case for compatibility
    const keyValueData = extractedData?.keyValuePairs || extractedData?.key_value_pairs;
    
    if (keyValueData) {
      try {
        const kvPairs = typeof keyValueData === 'string' 
          ? JSON.parse(keyValueData)
          : keyValueData;
        
        const keys = Object.keys(kvPairs).filter(key => {
          const value = kvPairs[key];
          // Filter keys that might contain dates
          return typeof value === 'string' && (
            /\d/.test(value) || // Contains numbers
            /date|time|day|month|year|audit/i.test(key) // Key suggests date
          );
        });
        setAvailableKeys(keys);
      } catch (error) {
        console.error('Error parsing key-value pairs:', error);
      }
    }
    
    // Extract available columns from table data
    if (extractedData?.tables && extractedData.tables.length > 0) {
      const firstTable = extractedData.tables[0];
      if (firstTable.rows && firstTable.rows.length > 0) {
        const headers = firstTable.rows[0].cells || [];
        setAvailableColumns(headers.map((cell, index) => ({
          index,
          name: cell.content || `Column ${index + 1}`,
          content: cell.content
        })));
      }
    }
  }, [extractedData]);

  // Update parent component when config changes
  useEffect(() => {
    onDateConfigChange(dateConfig);
  }, [dateConfig]); // Remove onDateConfigChange from dependencies

  // Preview date extraction
  const previewExtraction = () => {
    if (!extractedData) {
      setPreviewResult({ error: 'No data available for preview' });
      return;
    }

    try {
      let extractedDate = null;
      let source = null;

      if (dateConfig.sourceType === 'json' && dateConfig.jsonKey) {
        // Extract from JSON key-value pairs (check both camelCase and snake_case)
        const keyValueData = extractedData.keyValuePairs || extractedData.key_value_pairs;
        const kvPairs = typeof keyValueData === 'string' 
          ? JSON.parse(keyValueData)
          : keyValueData;
        
        const normalizedKey = dateConfig.jsonKey.toLowerCase().replace(/\s/g, '');
        const normalizedKvPairs = Object.keys(kvPairs).reduce((acc, key) => {
          acc[key.toLowerCase().replace(/\s/g, '')] = kvPairs[key];
          return acc;
        }, {});
        
        extractedDate = normalizedKvPairs[normalizedKey];
        source = `JSON key: "${dateConfig.jsonKey}"`;
        
              } else if (dateConfig.sourceType === 'text' && (dateConfig.textPattern || dateConfig.regexPattern)) {
        // Extract from text using pattern
        const text = extractedData.extractedText || '';
        
        if (dateConfig.regexPattern) {
          // Use regex pattern with multiline + dotAll flags to mirror backend behavior
          const regexFlags = 'ims';
          const regex = new RegExp(dateConfig.regexPattern, regexFlags);
          const match = text.match(regex);
          
          if (match) {
            // Special handling for multi-line date patterns
            if (dateConfig.regexPattern.includes('\\n') && match.length > 3) {
              // For patterns like Month\nDay\nYear, reconstruct the date
              const month = match[1] || match[2]; // Month might be in different position
              const day = match[2] || match[3];
              const year = match[3] || match[4];
              
              if (month && day && year) {
                extractedDate = `${month} ${day} ${year}`;
                source = `Regex pattern (multi-line): Extracted "${month} ${day} ${year}"`;
              } else {
                extractedDate = match[1] || match[0];
                source = `Regex pattern: "${dateConfig.regexPattern}"`;
              }
            } else if (match.groups && match.groups.month && match.groups.day && match.groups.year) {
              // Named groups month/day/year (any-order regex)
              extractedDate = `${match.groups.month} ${match.groups.day} ${match.groups.year}`;
              source = `Regex pattern (components): Extracted "${match.groups.month} ${match.groups.day} ${match.groups.year}"`;
            } else {
              extractedDate = match[1] || match[0]; // Use capture group if available
              source = `Regex pattern: "${dateConfig.regexPattern}"`;
            }
          }
        } else if (dateConfig.componentExtraction) {
          // Extract date components from multiple lines
          const components = dateConfig.dateComponents;
          const lines = text.split('\n');
          let monthValue = '', dayValue = '', yearValue = '';
          
          if (components.extractionMode === 'fuzzy') {
            // Fuzzy component extraction - find components in any order within a window
            const basePattern = components.basePattern || 'DAILY FLASH REPORT';
            let baseLineIndex = -1;
            
            // Find the base pattern line
            for (let i = 0; i < lines.length; i++) {
              if (lines[i].toLowerCase().includes(basePattern.toLowerCase())) {
                baseLineIndex = i;
                break;
              }
            }
            
            if (baseLineIndex >= 0) {
              // Search in a window around the base pattern (10 lines before and after)
              const windowStart = Math.max(0, baseLineIndex - 5);
              const windowEnd = Math.min(lines.length, baseLineIndex + 15);
              const windowLines = lines.slice(windowStart, windowEnd);
              
              // Define patterns for each component
              const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                                 'july', 'august', 'september', 'october', 'november', 'december'];
              const monthRegex = new RegExp(`\\b(${monthNames.join('|')})\\b`, 'i');
              const dayRegex = /\b([1-9]|[12][0-9]|3[01])\b/;
              const yearRegex = /\b(20[0-9]{2})\b/;
              
              // Find components in the window
              for (const line of windowLines) {
                const trimmedLine = line.trim();
                if (!monthValue && monthRegex.test(trimmedLine)) {
                  const match = trimmedLine.match(monthRegex);
                  if (match) monthValue = match[1];
                }
                if (!dayValue && dayRegex.test(trimmedLine)) {
                  const match = trimmedLine.match(dayRegex);
                  if (match && !yearRegex.test(trimmedLine)) { // Don't match day if line contains year
                    dayValue = match[1];
                  }
                }
                if (!yearValue && yearRegex.test(trimmedLine)) {
                  const match = trimmedLine.match(yearRegex);
                  if (match) yearValue = match[1];
                }
              }
              
              if (monthValue && dayValue && yearValue) {
                // Capitalize month name properly
                monthValue = monthValue.charAt(0).toUpperCase() + monthValue.slice(1).toLowerCase();
                extractedDate = `${monthValue} ${dayValue}, ${yearValue}`;
                source = `Fuzzy extraction after "${basePattern}": Found Month="${monthValue}", Day="${dayValue}", Year="${yearValue}" within window`;
              }
            }
          } else if (components.extractionMode === 'sequential') {
            // Sequential extraction mode
            const basePattern = components.basePattern;
            if (basePattern) {
              // Find the base pattern line
              for (let i = 0; i < lines.length; i++) {
                if (lines[i].toLowerCase().includes(basePattern.toLowerCase())) {
                  // Found base pattern, extract components at specified offsets
                  const monthLineIndex = i + components.monthOffset;
                  const dayLineIndex = i + components.dayOffset;
                  const yearLineIndex = i + components.yearOffset;
                  
                  if (monthLineIndex < lines.length) {
                    monthValue = lines[monthLineIndex].trim();
                  }
                  if (dayLineIndex < lines.length) {
                    dayValue = lines[dayLineIndex].trim();
                  }
                  if (yearLineIndex < lines.length) {
                    yearValue = lines[yearLineIndex].trim();
                  }
                  
                  break;
                }
              }
              
              if (monthValue && dayValue && yearValue) {
                // Format the combined date string based on the selected format
                if (dateConfig.dateFormat === '%B %d %Y') {
                  extractedDate = `${monthValue} ${dayValue} ${yearValue}`;
                } else {
                  extractedDate = `${monthValue} ${dayValue}, ${yearValue}`;
                }
                source = `Sequential extraction after "${basePattern}": Month="${monthValue}" (+${components.monthOffset}), Day="${dayValue}" (+${components.dayOffset}), Year="${yearValue}" (+${components.yearOffset})`;
              }
            }
          } else {
            // Pattern-based extraction mode (existing logic)
            // Extract month
            if (components.monthPattern) {
              for (const line of lines) {
                if (line.toLowerCase().includes(components.monthPattern.toLowerCase())) {
                  if (components.monthPattern.includes(':')) {
                    const parts = line.split(':', 1);
                    if (parts.length > 1) monthValue = parts[1].trim();
                  } else {
                    monthValue = components.monthPattern; // Exact match
                  }
                  break;
                }
              }
            }
            
            // Extract day
            if (components.dayPattern) {
              for (const line of lines) {
                if (line.toLowerCase().includes(components.dayPattern.toLowerCase())) {
                  if (components.dayPattern.includes(':')) {
                    const parts = line.split(':', 1);
                    if (parts.length > 1) dayValue = parts[1].trim();
                  } else {
                    dayValue = components.dayPattern; // Exact match
                  }
                  break;
                }
              }
            }
            
            // Extract year
            if (components.yearPattern) {
              for (const line of lines) {
                if (line.toLowerCase().includes(components.yearPattern.toLowerCase())) {
                  if (components.yearPattern.includes(':')) {
                    const parts = line.split(':', 1);
                    if (parts.length > 1) yearValue = parts[1].trim();
                  } else {
                    yearValue = components.yearPattern; // Exact match
                  }
                  break;
                }
              }
            }
            
            if (monthValue && dayValue && yearValue) {
              // Format the combined date string based on the selected format
              if (dateConfig.dateFormat === '%B %d %Y') {
                extractedDate = `${monthValue} ${dayValue} ${yearValue}`;
              } else {
                extractedDate = `${monthValue} ${dayValue}, ${yearValue}`;
              }
              source = `Pattern-based component extraction: Month="${monthValue}", Day="${dayValue}", Year="${yearValue}"`;
            }
          }
          
        } else if (dateConfig.textPattern) {
          // Use text pattern (single or multi-line)
          if (dateConfig.multiLine && dateConfig.textPattern.includes('\n')) {
            // Multi-line pattern matching
            const patternLines = dateConfig.textPattern.split('\n').filter(line => line.trim());
            const textLines = text.split('\n');
            
            // Find the start of the pattern
            for (let i = 0; i < textLines.length; i++) {
              if (patternLines[0].toLowerCase().trim() && 
                  textLines[i].toLowerCase().includes(patternLines[0].toLowerCase().trim())) {
                // Found the first line of the pattern
                let patternMatch = true;
                let currentLineIdx = i;
                
                // Check if all pattern lines match sequentially
                for (let j = 0; j < patternLines.length; j++) {
                  if (currentLineIdx + j >= textLines.length ||
                      !textLines[currentLineIdx + j].toLowerCase().includes(patternLines[j].toLowerCase().trim())) {
                    patternMatch = false;
                    break;
                  }
                }
                
                if (patternMatch) {
                  // Found the complete pattern, look for date after the last pattern line
                  const lastPatternLineIdx = currentLineIdx + patternLines.length - 1;
                  const lastPatternLine = textLines[lastPatternLineIdx];
                  
                  // Try to extract date from the end of the last pattern line
                  if (lastPatternLine.includes(':')) {
                    const parts = lastPatternLine.split(':', 1);
                    if (parts.length > 1 && parts[1].trim()) {
                      extractedDate = parts[1].trim();
                      break;
                    }
                  }
                  
                  // If no date on pattern line, check the next line
                  if (!extractedDate && lastPatternLineIdx + 1 < textLines.length) {
                    const nextLine = textLines[lastPatternLineIdx + 1].trim();
                    if (nextLine) {
                      extractedDate = nextLine;
                      break;
                    }
                  }
                }
              }
            }
            source = `Multi-line text pattern: "${dateConfig.textPattern.replace(/\n/g, ' → ')}"`;
          } else {
            // Single line pattern matching (original logic)
            const lines = text.split('\n');
            
            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];
              if (line.toLowerCase().includes(dateConfig.textPattern.toLowerCase())) {
                // First try to extract date from the same line after the pattern
                if (line.includes(':')) {
                  const parts = line.split(':', 1);
                  if (parts.length > 1 && parts[1].trim()) {
                    extractedDate = parts[1].trim();
                    break;
                  }
                }
                
                // Try splitting by the pattern itself
                const parts = line.split(dateConfig.textPattern, 1);
                if (parts.length > 1 && parts[1].trim()) {
                  extractedDate = parts[1].trim();
                  break;
                }
                
                // If no date on same line, check the next line
                if (i + 1 < lines.length) {
                  const nextLine = lines[i + 1].trim();
                  if (nextLine) {
                    extractedDate = nextLine;
                    break;
                  }
                }
              }
            }
            source = `Single-line text pattern: "${dateConfig.textPattern}"`;
          }
        }
        
              } else if (dateConfig.sourceType === 'table' && dateConfig.tableColumn !== undefined) {
        // Extract from table column
        const tables = extractedData.tables || [];
        if (tables.length > 0 && tables[0].rows && tables[0].rows.length > 1) {
          const dataRow = tables[0].rows[1]; // First data row (skip header)
          if (dataRow.cells && dataRow.cells[dateConfig.tableColumn]) {
            extractedDate = dataRow.cells[dateConfig.tableColumn].content;
            source = `Table column ${dateConfig.tableColumn + 1}`;
          }
        }
        
      } else if (dateConfig.sourceType === 'positional' && dateConfig.positionalConfig) {
        // Extract from positional selection
        const text = extractedData.extractedText || '';
        const config = dateConfig.positionalConfig;
        
        if (config.searchMethod === 'contextual') {
          // Use contextual search
          const beforeContext = config.beforeContext || '';
          const afterContext = config.afterContext || '';
          const searchWindow = config.searchWindow || 100;
          const originalStart = config.start || 0;
          
          // Define search area around original position
          const searchStart = Math.max(0, originalStart - searchWindow);
          const searchEnd = Math.min(text.length, originalStart + searchWindow);
          const searchArea = text.substring(searchStart, searchEnd);
          
          // Find date using context anchors
          let foundDate = null;
          
          if (beforeContext && afterContext) {
            // Use both before and after context
            const beforeIndex = searchArea.toLowerCase().indexOf(beforeContext.toLowerCase());
            const afterIndex = searchArea.toLowerCase().indexOf(afterContext.toLowerCase());
            
            if (beforeIndex !== -1 && afterIndex !== -1 && afterIndex > beforeIndex) {
              const dateStart = beforeIndex + beforeContext.length;
              const dateEnd = afterIndex;
              foundDate = searchArea.substring(dateStart, dateEnd).trim();
            }
          } else if (beforeContext) {
            // Use only before context
            const beforeIndex = searchArea.toLowerCase().indexOf(beforeContext.toLowerCase());
            if (beforeIndex !== -1) {
              const remainingText = searchArea.substring(beforeIndex + beforeContext.length);
              // Look for date pattern in next 50 characters
              const dateMatch = remainingText.match(/\d{1,2}[-\/]\w{3}[-\/]\d{2,4}|\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}/);
              if (dateMatch) {
                foundDate = dateMatch[0];
              }
            }
          } else {
            // Fallback to original exact position
            foundDate = config.selectedText;
          }
          
          if (foundDate) {
            extractedDate = foundDate;
            source = `Context search: "${foundDate}" (using ${beforeContext ? 'before' : ''}${beforeContext && afterContext ? ' and ' : ''}${afterContext ? 'after' : ''} context)`;
          }
        } else {
          // Use exact position (original method)
          const { start, end } = config;
          if (start !== undefined && end !== undefined && text.length > end) {
            extractedDate = text.substring(start, end);
            source = `Exact position ${start}-${end}: "${config.selectedText}"`;
          }
        }
      } else if (dateConfig.sourceType === 'current') {
        // Use current date with offset
        const now = new Date();
        const offset = dateConfig.currentDateConfig.dateOffset || 0;
        const targetDate = new Date(now.getTime() + offset * 24 * 60 * 60 * 1000);

        if (dateConfig.currentDateConfig.timeZone === 'utc') {
          extractedDate = targetDate.toISOString().split('T')[0];
        } else if (dateConfig.currentDateConfig.timeZone === 'local') {
          extractedDate = targetDate.toLocaleDateString();
        } else {
          try {
            extractedDate = targetDate.toLocaleDateString('en-US', {
              timeZone: dateConfig.currentDateConfig.timeZone,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            });
          } catch {
            extractedDate = targetDate.toLocaleDateString();
          }
        }

        source = `Current date with ${offset} day offset in ${dateConfig.currentDateConfig.timeZone} timezone`;
      }

      if (extractedDate) {
        setPreviewResult({
          success: true,
          extractedValue: extractedDate,
          source,
          format: dateConfig.dateFormat,
          // Note: Actual date parsing would be done server-side
          parsedDate: 'Preview parsing on server-side'
        });
      } else {
        setPreviewResult({
          error: 'No date found with current configuration',
          source: dateConfig.sourceType
        });
      }
      
    } catch (error) {
      setPreviewResult({
        error: `Preview error: ${error.message}`
      });
    }
  };

  const updateConfig = (updates) => {
    setDateConfig(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-2">
        <Calendar className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Date Extraction Configuration
        </h3>
      </div>

      {/* Source Type Selection */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <button
          onClick={() => updateConfig({ sourceType: 'json' })}
          className={`p-4 border-2 rounded-lg text-center transition-colors ${
            dateConfig.sourceType === 'json'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
          }`}
        >
          <Database className="w-6 h-6 mx-auto mb-2 text-blue-600" />
          <div className="font-medium">JSON Key</div>
          <div className="cp-help">Extract from form fields</div>
        </button>

        <button
          onClick={() => updateConfig({ sourceType: 'text' })}
          className={`p-4 border-2 rounded-lg text-center transition-colors ${
            dateConfig.sourceType === 'text'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
          }`}
        >
          <FileText className="w-6 h-6 mx-auto mb-2 text-green-600" />
          <div className="font-medium">Text Pattern</div>
          <div className="cp-help">Search in extracted text</div>
        </button>

        <button
          onClick={() => updateConfig({
            sourceType: 'filename',
            regexPattern: dateConfig.regexPattern || '(\\d{4}-\\d{2}-\\d{2})',
            dateFormat: dateConfig.sourceType === 'filename' ? dateConfig.dateFormat : '%Y-%m-%d',
          })}
          className={`p-4 border-2 rounded-lg text-center transition-colors ${
            dateConfig.sourceType === 'filename'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
          }`}
        >
          <FileSearch className="w-6 h-6 mx-auto mb-2 text-teal-600" />
          <div className="font-medium">File Name</div>
          <div className="cp-help">Read the date in the file's name</div>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <button
          onClick={() => updateConfig({ sourceType: 'table' })}
          className={`p-4 border-2 rounded-lg text-center transition-colors ${
            dateConfig.sourceType === 'table'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
          }`}
        >
          <Settings className="w-6 h-6 mx-auto mb-2 text-purple-600" />
          <div className="font-medium">Table Column</div>
          <div className="cp-help">Extract from table data</div>
        </button>

        <button
          onClick={() => updateConfig({ sourceType: 'positional' })}
          className={`p-4 border-2 rounded-lg text-center transition-colors ${
            dateConfig.sourceType === 'positional'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
          }`}
        >
          <Target className="w-6 h-6 mx-auto mb-2 text-orange-600" />
          <div className="font-medium">Highlight Position</div>
          <div className="cp-help">Select text manually</div>
        </button>

        <button
          onClick={() => updateConfig({ sourceType: 'current' })}
          className={`p-4 border-2 rounded-lg text-center transition-colors ${
            dateConfig.sourceType === 'current'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
          }`}
        >
          <Clock className="w-6 h-6 mx-auto mb-2 text-indigo-600" />
          <div className="font-medium">Current Date</div>
          <div className="cp-help">Use today's date</div>
        </button>
      </div>

      {/* Source-specific Configuration */}
      {dateConfig.sourceType === 'json' && (
        <div className="space-y-4">
          <div>
            <label className="cp-label">
              JSON Key
            </label>
            <select
              value={dateConfig.jsonKey}
              onChange={(e) => updateConfig({ jsonKey: e.target.value })}
              className="cp-input"
            >
              <option value="">Select a key...</option>
              {availableKeys.map(key => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Choose the JSON key that contains the date value
            </p>
          </div>
        </div>
      )}

      {dateConfig.sourceType === 'text' && (
        <div className="space-y-4">
          <div>
            <label className="cp-label">
              Pattern Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => updateConfig({ useRegex: false })}
                className={`p-3 border rounded-lg text-center ${
                  !dateConfig.useRegex
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600'
                }`}
              >
                Simple Text Search
              </button>
              <button
                onClick={() => updateConfig({ useRegex: true })}
                className={`p-3 border rounded-lg text-center ${
                  dateConfig.useRegex
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600'
                }`}
              >
                <Code className="w-4 h-4 inline mr-1" />
                Regex Pattern
              </button>
            </div>
          </div>

          {dateConfig.useRegex ? (
            <div>
              <label className="cp-label">
                Regular Expression Pattern
              </label>
              <select
                value={dateConfig.regexPattern}
                onChange={(e) => {
                  const pattern = e.target.value;
                  const updates = { regexPattern: pattern };
                  
                  // Set appropriate date format for multi-line patterns
                  if (pattern.includes('(January|February|March|April|May|June|July|August|September|October|November|December)\\s*\\n\\s*(\\d{1,2})\\s*\\n\\s*(\\d{4})')) {
                    // Month Day Year on separate lines
                    updates.dateFormat = '%B %d %Y';
                  } else if (pattern.includes('(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\\s*\\n\\s*(January|February|March|April|May|June|July|August|September|October|November|December)\\s*\\n\\s*(\\d{1,2})\\s*\\n\\s*(\\d{4})')) {
                    // Multi-line with weekday
                    updates.dateFormat = '%B %d %Y';
                  } else if (pattern.includes('(?=.*\\b(January|February|March|April|May|June|July|August|September|October|November|December)\\b)(?=.*\\b(\\d{1,2})\\b)(?=.*\\b(\\d{4})\\b)')) {
                    // Any-order components via lookaheads (captures Month, Day, Year in groups 1-3)
                    updates.dateFormat = '%B %d %Y';
                  } else if (pattern === '(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),\\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\\s+\\d{1,2},\\s+\\d{4}') {
                    // Full Day, Month Day, Year with commas
                    updates.dateFormat = '%A %B %d, %Y';
                  }
                  
                  updateConfig(updates);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2"
              >
                <option value="">Select a pattern...</option>
                {regexPatterns.map((pattern, index) => (
                  <option key={index} value={pattern.value}>
                    {pattern.label}
                  </option>
                ))}
              </select>
              {dateConfig.regexPattern && (
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {regexPatterns.find(p => p.value === dateConfig.regexPattern)?.description}
                  </p>
                  <code className="text-xs text-blue-600 dark:text-blue-400 mt-1 block">
                    {dateConfig.regexPattern}
                  </code>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="cp-label">
                Text Pattern
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="patternType"
                      value="single"
                      checked={!dateConfig.multiLine && !dateConfig.componentExtraction}
                      onChange={() => updateConfig({ multiLine: false, componentExtraction: false })}
                      className="mr-2"
                    />
                    <span className="text-sm">Single Line</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="patternType"
                      value="multi"
                      checked={dateConfig.multiLine && !dateConfig.componentExtraction}
                      onChange={() => updateConfig({ multiLine: true, componentExtraction: false })}
                      className="mr-2"
                    />
                    <span className="text-sm">Multi-Line Pattern</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="patternType"
                      value="component"
                      checked={dateConfig.componentExtraction}
                      onChange={() => updateConfig({ componentExtraction: true, multiLine: false })}
                      className="mr-2"
                    />
                    <span className="text-sm">Component Extraction</span>
                  </label>
                </div>
                
                {dateConfig.componentExtraction ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                        Date Component Extraction
                      </h4>
                      <p className="text-xs text-blue-700 dark:text-blue-400 mb-4">
                        Extract date components (month, day, year) from different lines and combine them into a single date.
                      </p>
                      
                      {/* Extraction Mode Selection */}
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">
                          Component Extraction Mode
                        </label>
                        <div className="flex space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="componentMode"
                              value="pattern"
                              checked={dateConfig.dateComponents.extractionMode === 'pattern'}
                              onChange={() => updateConfig({
                                dateComponents: {
                                  ...dateConfig.dateComponents,
                                  extractionMode: 'pattern'
                                }
                              })}
                              className="mr-2"
                            />
                            <span className="text-xs text-blue-800 dark:text-blue-200">Pattern-based</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="componentMode"
                              value="sequential"
                              checked={dateConfig.dateComponents.extractionMode === 'sequential'}
                              onChange={() => updateConfig({
                                dateComponents: {
                                  ...dateConfig.dateComponents,
                                  extractionMode: 'sequential'
                                }
                              })}
                              className="mr-2"
                            />
                            <span className="text-xs text-blue-800 dark:text-blue-200">Sequential</span>
                          </label>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          {dateConfig.dateComponents.extractionMode === 'sequential' 
                            ? 'Extract components from consecutive lines after a base pattern'
                            : 'Extract components using individual patterns for each component'
                          }
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        {dateConfig.dateComponents.extractionMode === 'sequential' ? (
                          /* Sequential Mode UI */
                          <>
                            <div>
                              <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                Base Pattern
                              </label> 
                              <input
                                type="text"
                                value={dateConfig.dateComponents.basePattern}
                                onChange={(e) => updateConfig({
                                  dateComponents: {
                                    ...dateConfig.dateComponents,
                                    basePattern: e.target.value
                                  }
                                })}
                                placeholder="e.g., 'Rooms' - text to find before extracting date components"
                                className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                              />
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                Find this text, then extract date components from the following lines
                              </p>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                  Month Line Offset
                                </label>
                                <select
                                  value={dateConfig.dateComponents.monthOffset}
                                  onChange={(e) => updateConfig({
                                    dateComponents: {
                                      ...dateConfig.dateComponents,
                                      monthOffset: parseInt(e.target.value)
                                    }
                                  })}
                                  className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                                >
                                  <option value={1}>Next line (+1)</option>
                                  <option value={2}>2 lines after (+2)</option>
                                  <option value={3}>3 lines after (+3)</option>
                                  <option value={4}>4 lines after (+4)</option>
                                  <option value={5}>5 lines after (+5)</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                  Day Line Offset
                                </label>
                                <select
                                  value={dateConfig.dateComponents.dayOffset}
                                  onChange={(e) => updateConfig({
                                    dateComponents: {
                                      ...dateConfig.dateComponents,
                                      dayOffset: parseInt(e.target.value)
                                    }
                                  })}
                                  className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                                >
                                  <option value={1}>Next line (+1)</option>
                                  <option value={2}>2 lines after (+2)</option>
                                  <option value={3}>3 lines after (+3)</option>
                                  <option value={4}>4 lines after (+4)</option>
                                  <option value={5}>5 lines after (+5)</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                  Year Line Offset
                                </label>
                                <select
                                  value={dateConfig.dateComponents.yearOffset}
                                  onChange={(e) => updateConfig({
                                    dateComponents: {
                                      ...dateConfig.dateComponents,
                                      yearOffset: parseInt(e.target.value)
                                    }
                                  })}
                                  className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                                >
                                  <option value={1}>Next line (+1)</option>
                                  <option value={2}>2 lines after (+2)</option>
                                  <option value={3}>3 lines after (+3)</option>
                                  <option value={4}>4 lines after (+4)</option>
                                  <option value={5}>5 lines after (+5)</option>
                                </select>
                              </div>
                            </div>

                            <div className="p-3 bg-blue-100 dark:bg-blue-800/30 rounded text-xs text-blue-700 dark:text-blue-300">
                              <strong>Example:</strong> If base pattern is "Rooms" and you set Month=+1, Day=+2, Year=+3, then:
                              <br />• Find "Rooms" → Next line is Month → 2nd line after is Day → 3rd line after is Year
                            </div>
                          </>
                        ) : (
                          /* Pattern Mode UI (existing) */
                          <>
                        {/* Month Component */}
                        <div>
                          <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                            Month Line Pattern
                          </label> 
                          <input
                            type="text"
                            value={dateConfig.dateComponents.monthPattern}
                            onChange={(e) => updateConfig({
                              dateComponents: {
                                ...dateConfig.dateComponents,
                                monthPattern: e.target.value
                              }
                            })}
                            placeholder="e.g., August (exact text) or pattern like 'Month:' to extract what follows"
                            className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                          />
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Pattern to find the month value (e.g., "August", "08", "Jan")
                          </p>
                        </div>

                        {/* Day Component */}
                        <div>
                          <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                            Day Line Pattern
                          </label>
                          <input
                            type="text"
                            value={dateConfig.dateComponents.dayPattern}
                            onChange={(e) => updateConfig({
                              dateComponents: {
                                ...dateConfig.dateComponents,
                                dayPattern: e.target.value
                              }
                            })}
                            placeholder="e.g., 3 (exact text) or pattern like 'Day:' to extract what follows"
                            className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                          />
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Pattern to find the day value (e.g., "3", "15", "03")
                          </p>
                        </div>

                        {/* Year Component */}
                        <div>
                          <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                            Year Line Pattern
                          </label>
                          <input
                            type="text"
                            value={dateConfig.dateComponents.yearPattern}
                            onChange={(e) => updateConfig({
                              dateComponents: {
                                ...dateConfig.dateComponents,
                                yearPattern: e.target.value
                              }
                            })}
                            placeholder="e.g., 2025 (exact text) or pattern like 'Year:' to extract what follows"
                            className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                          />
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Pattern to find the year value (e.g., "2025", "25")
                          </p>
                        </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : dateConfig.multiLine ? (
                  <div>
                    <textarea
                      value={dateConfig.textPattern}
                      onChange={(e) => updateConfig({ textPattern: e.target.value })}
                      placeholder="Enter multi-line pattern, e.g.:&#10;DAILY FLASH REPORT&#10;August, 2025&#10;Occupancy % A.D.R."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-24 resize-vertical"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter text pattern that spans multiple lines. Each line will be matched sequentially.
                    </p>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={dateConfig.textPattern}
                      onChange={(e) => updateConfig({ textPattern: e.target.value })}
                      placeholder="e.g., Date:, Business Date, Night Audit"
                      className="cp-input"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter text that precedes the date in the document
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {dateConfig.sourceType === 'table' && (
        <div>
          <label className="cp-label">
            Table Column
          </label>
          <select
            value={dateConfig.tableColumn || ''}
            onChange={(e) => updateConfig({ tableColumn: parseInt(e.target.value) })}
            className="cp-input"
          >
            <option value="">Select a column...</option>
            {availableColumns.map((col, index) => (
              <option key={index} value={index}>
                Column {index + 1}: {col.content || 'No header'}
              </option>
            ))}
          </select>
        </div>
      )}

      {dateConfig.sourceType === 'positional' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Select Date Position in Text
          </label>
          {extractedData?.extractedText ? (
            <div className="space-y-4">
              <TextHighlighter
                textContent={extractedData.extractedText}
                onPositionSelect={(positionData) => {
                  updateConfig({ positionalConfig: positionData });
                  setIsSelectingPosition(false);
                }}
                existingPosition={dateConfig.positionalConfig}
                isSelectingPosition={isSelectingPosition}
                onCancelSelection={() => setIsSelectingPosition(false)}
              />
              
              {/* Flexibility Options */}
              {dateConfig.positionalConfig && (
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-3">
                    Search Flexibility Options
                  </h4>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mb-4">
                    Make the date extraction more flexible to handle slight document variations
                  </p>
                  
                  <div className="space-y-4">
                    {/* Search Method */}
                    <div>
                      <label className="block text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                        Search Method
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="searchMethod"
                            value="exact"
                            checked={dateConfig.positionalConfig.searchMethod !== 'contextual'}
                            onChange={(e) => updateConfig({
                              positionalConfig: {
                                ...dateConfig.positionalConfig,
                                searchMethod: 'exact'
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-blue-800 dark:text-blue-200">Exact Position</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="searchMethod"
                            value="contextual"
                            checked={dateConfig.positionalConfig.searchMethod === 'contextual'}
                            onChange={(e) => updateConfig({
                              positionalConfig: {
                                ...dateConfig.positionalConfig,
                                searchMethod: 'contextual'
                              }
                            })}
                            className="mr-2"
                          />
                          <span className="text-sm text-blue-800 dark:text-blue-200">Context Search</span>
                        </label>
                      </div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        {dateConfig.positionalConfig.searchMethod === 'contextual' 
                          ? 'Search for date using surrounding text as anchor points'
                          : 'Use exact character positions (fragile to document changes)'
                        }
                      </p>
                    </div>

                    {/* Context Search Options */}
                    {dateConfig.positionalConfig.searchMethod === 'contextual' && (
                      <div className="space-y-3 pl-4 border-l-2 border-blue-300">
                        <div>
                          <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                            Before Text (Context Anchor)
                          </label>
                          <input
                            type="text"
                            value={dateConfig.positionalConfig.beforeContext || dateConfig.positionalConfig.context?.before?.slice(-20) || ''}
                            onChange={(e) => updateConfig({
                              positionalConfig: {
                                ...dateConfig.positionalConfig,
                                beforeContext: e.target.value
                              }
                            })}
                            placeholder="Text that appears before the date"
                            className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                          />
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Will search for dates that appear after this text
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                            After Text (Context Anchor)
                          </label>
                          <input
                            type="text"
                            value={dateConfig.positionalConfig.afterContext || dateConfig.positionalConfig.context?.after?.slice(0, 20) || ''}
                            onChange={(e) => updateConfig({
                              positionalConfig: {
                                ...dateConfig.positionalConfig,
                                afterContext: e.target.value
                              }
                            })}
                            placeholder="Text that appears after the date"
                            className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                          />
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Will search for dates that appear before this text
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                            Search Window Size
                          </label>
                          <select
                            value={dateConfig.positionalConfig.searchWindow || 100}
                            onChange={(e) => updateConfig({
                              positionalConfig: {
                                ...dateConfig.positionalConfig,
                                searchWindow: parseInt(e.target.value)
                              }
                            })}
                            className="w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                          >
                            <option value={50}>50 characters</option>
                            <option value={100}>100 characters</option>
                            <option value={200}>200 characters</option>
                            <option value={500}>500 characters</option>
                          </select>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            How far from the original position to search
                          </p>
                        </div>

                        <div>
                          <label className="flex items-center text-xs text-blue-800 dark:text-blue-200">
                            <input
                              type="checkbox"
                              checked={dateConfig.positionalConfig.allowMultipleMatches || false}
                              onChange={(e) => updateConfig({
                                positionalConfig: {
                                  ...dateConfig.positionalConfig,
                                  allowMultipleMatches: e.target.checked
                                }
                              })}
                              className="mr-2"
                            />
                            Use first match if multiple dates found
                          </label>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 ml-5">
                            When enabled, uses the first date found within the search criteria
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                No extracted text available. Please upload a file first.
              </p>
            </div>
          )}
        </div>
      )}

      {dateConfig.sourceType === 'filename' && (
        <div className="space-y-4">
          <div>
            <label className="cp-label">
              Date pattern in the file name
            </label>
            <input
              type="text"
              value={dateConfig.regexPattern || ''}
              onChange={(e) => updateConfig({ regexPattern: e.target.value })}
              placeholder="(\d{4}-\d{2}-\d{2})"
              className="cp-input font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">
              A regular expression; the first capture group is the date, read with the date format below.
              For a file named <code>BWH_RMS_2026-09-23.csv</code> use <code>(\d{4}-\d{2}-\d{2})</code> with <code>%Y-%m-%d</code>.
              If a file name has no match, the processing date is used.
            </p>
          </div>
        </div>
      )}

      {dateConfig.sourceType === 'current' && (
        <div className="space-y-4">
          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
            <h4 className="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-3 flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Current Date Configuration
            </h4>
            <p className="text-xs text-indigo-700 dark:text-indigo-400 mb-4">
              Use the current date at the time of processing, with optional adjustments.
            </p>

            <div className="space-y-4">
              {/* Date Offset */}
              <div>
                <label className="block text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">
                  Date Offset (Days)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={dateConfig.currentDateConfig.dateOffset}
                    onChange={(e) => updateConfig({
                      currentDateConfig: {
                        ...dateConfig.currentDateConfig,
                        dateOffset: parseInt(e.target.value) || 0
                      }
                    })}
                    placeholder="0"
                    className="w-24 px-3 py-2 border border-indigo-300 dark:border-indigo-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100"
                  />
                  <span className="text-sm text-indigo-700 dark:text-indigo-300">
                    days {dateConfig.currentDateConfig.dateOffset >= 0 ? 'from now' : 'ago'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                  Positive values = future dates, negative values = past dates. 0 = today.
                </p>
              </div>

              {/* Timezone Selection */}
              <div>
                <label className="block text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">
                  Timezone
                </label>
                <select
                  value={dateConfig.currentDateConfig.timeZone}
                  onChange={(e) => updateConfig({
                    currentDateConfig: {
                      ...dateConfig.currentDateConfig,
                      timeZone: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 border border-indigo-300 dark:border-indigo-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100"
                >
                  <option value="local">Local Time</option>
                  <option value="utc">UTC</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Paris (CET)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Australia/Sydney">Sydney (AEST)</option>
                </select>
                <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                  Determines the timezone for "current date" calculation
                </p>
              </div>

              {/* Preview */}
              <div className="p-3 bg-indigo-100 dark:bg-indigo-800/30 rounded-md">
                <p className="text-sm text-indigo-800 dark:text-indigo-200 font-medium">Preview:</p>
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  {(() => {
                    const now = new Date();
                    const offset = dateConfig.currentDateConfig.dateOffset || 0;
                    const targetDate = new Date(now.getTime() + offset * 24 * 60 * 60 * 1000);

                    if (dateConfig.currentDateConfig.timeZone === 'utc') {
                      return `${targetDate.toISOString().split('T')[0]} (UTC)`;
                    } else if (dateConfig.currentDateConfig.timeZone === 'local') {
                      return `${targetDate.toLocaleDateString()} (Local Time)`;
                    } else {
                      try {
                        return targetDate.toLocaleDateString('en-US', {
                          timeZone: dateConfig.currentDateConfig.timeZone,
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        }) + ` (${dateConfig.currentDateConfig.timeZone})`;
                      } catch {
                        return `${targetDate.toLocaleDateString()} (${dateConfig.currentDateConfig.timeZone})`;
                      }
                    }
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Format Configuration */}
      <div className="space-y-4">
        <div>
          <label className="cp-label">
            Date Format
          </label>
          <select
            value={dateConfig.dateFormat}
            onChange={(e) => updateConfig({ dateFormat: e.target.value })}
            className="cp-input"
          >
            {formatPatterns.map((format, index) => (
              <option key={index} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>

          {dateConfig.dateFormat === 'custom' && (
            <input
              type="text"
              value={dateConfig.customFormat || ''}
              onChange={(e) => updateConfig({ customFormat: e.target.value, dateFormat: e.target.value })}
              placeholder="e.g., %d/%m/%Y or %B %d, %Y"
              className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          )}

          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Example:</strong> {formatPatterns.find(f => f.value === dateConfig.dateFormat)?.example || dateConfig.customFormat}
            </p>
          </div>
        </div>

        {/* Global Date Offset - applies to all extracted dates */}
        {dateConfig.sourceType !== 'current' && (
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
            <h4 className="text-sm font-medium text-purple-900 dark:text-purple-300 mb-3 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Date Offset Adjustment
            </h4>
            <p className="text-xs text-purple-700 dark:text-purple-400 mb-4">
              Adjust the extracted date by adding or subtracting days. Useful when the document shows today's date but the data is for yesterday.
            </p>

            <div>
              <label className="block text-sm font-medium text-purple-800 dark:text-purple-200 mb-2">
                Date Offset (Days)
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={dateConfig.dateOffset || 0}
                  onChange={(e) => updateConfig({
                    dateOffset: parseInt(e.target.value) || 0
                  })}
                  placeholder="0"
                  className="w-24 px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-purple-900 text-purple-900 dark:text-purple-100"
                />
                <span className="text-sm text-purple-700 dark:text-purple-300">
                  days {dateConfig.dateOffset >= 0 ? 'forward' : 'backward'}
                </span>
              </div>
              <p className="mt-1 text-xs text-purple-600 dark:text-purple-400">
                Positive values = add days (future), negative values = subtract days (past). Example: -1 for yesterday's data.
              </p>

              {dateConfig.dateOffset !== 0 && (
                <div className="mt-3 p-3 bg-purple-100 dark:bg-purple-800/30 rounded-md">
                  <p className="text-sm text-purple-800 dark:text-purple-200 font-medium">Example:</p>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    If extracted date is "Nov 04, 2025" and offset is {dateConfig.dateOffset},
                    the effective date will be "Nov {4 + dateConfig.dateOffset}, 2025"
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fallback Configuration */}
        {dateConfig.sourceType !== 'current' && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="fallbackEnabled"
                checked={dateConfig.fallbackEnabled}
                onChange={(e) => updateConfig({ fallbackEnabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="fallbackEnabled" className="text-sm text-gray-700 dark:text-gray-300">
                Enable OpenAI fallback if date extraction fails
              </label>
            </div>

            {dateConfig.fallbackEnabled && (
              <div className="ml-6 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md space-y-4">
                <div>
                  <label className="cp-label">
                    Row Range to Send to OpenAI
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Specify which rows to send to OpenAI for date extraction. Use this to skip problematic rows or focus on a specific section.
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="cp-label">
                        Start Row (0-indexed)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="999"
                        value={dateConfig.fallbackRowStart ?? 0}
                        onChange={(e) => {
                          const startRow = parseInt(e.target.value) || 0;
                          // Auto-update end row if it's less than start row
                          const endRow = Math.max(startRow + 1, dateConfig.fallbackRowEnd || 200);
                          updateConfig({
                            fallbackRowStart: startRow,
                            fallbackRowEnd: endRow
                          });
                        }}
                        className="cp-input"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        First row to include (0 = first line)
                      </p>
                    </div>

                    <div>
                      <label className="cp-label">
                        End Row (exclusive)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        value={dateConfig.fallbackRowEnd ?? 200}
                        onChange={(e) => {
                          const endRow = parseInt(e.target.value) || 200;
                          // Ensure end is greater than start
                          const startRow = Math.min(dateConfig.fallbackRowStart || 0, endRow - 1);
                          updateConfig({
                            fallbackRowStart: startRow,
                            fallbackRowEnd: endRow
                          });
                        }}
                        className="cp-input"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Last row (not included)
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs">
                    <p className="text-blue-700 dark:text-blue-300">
                      <strong>Will send rows {dateConfig.fallbackRowStart ?? 0} through {(dateConfig.fallbackRowEnd ?? 200) - 1}</strong>
                      {' '}({((dateConfig.fallbackRowEnd ?? 200) - (dateConfig.fallbackRowStart ?? 0))} total rows)
                    </p>
                    <p className="text-blue-600 dark:text-blue-400 mt-1">
                      Example: Start=5, End=10 sends rows 5, 6, 7, 8, 9 (skips rows 0-4 and 10+)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {dateConfig.sourceType === 'current' && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Note:</strong> When using "Current Date" as the source, the date will be generated at processing time and doesn't require format parsing.
            </p>
          </div>
        )}
      </div>

      {/* Preview Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">Preview</h4>
          <button
            onClick={previewExtraction}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Test Extraction
          </button>
        </div>

        {previewResult && (
          <div className={`p-4 rounded-md border ${
            previewResult.success 
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}>
            {previewResult.success ? (
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    Date extracted successfully
                  </p>
                  <div className="mt-2 text-sm text-green-700 dark:text-green-400 space-y-1">
                    <p><strong>Source:</strong> {previewResult.source}</p>
                    <p><strong>Extracted Value:</strong> "{previewResult.extractedValue}"</p>
                    <p><strong>Format:</strong> {previewResult.format}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">
                    Date extraction failed
                  </p>
                  <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                    {previewResult.error}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateExtractionConfig;